import {
  PROMOTIONS_CATEGORY_NAME,
  UNCATEGORIZED_CATEGORY_ID,
  UNCATEGORIZED_CATEGORY_NAME} from '../config'
import {
  CategoryTypeWithCounter,
  ProductCategoryType
} from '../types/categories'
import { AnyProductTypes, ProductTypes } from '../types/products'

export const rootCategories = (product: ProductTypes): ProductCategoryType =>
  product?.categoriesTree?.filter(category => category.parentId === null)[0]

export const lastCategories = (
  categories: ProductCategoryType[]
): ProductCategoryType => categories?.[categories?.length - 1]

export const sortCategory = (product: ProductTypes): ProductCategoryType[] =>
  product.categoriesTree?.sort((a, b) => a.depth - b.depth) || []

// Field-by-field (not spread) so extra API fields (description, seoDescription...)
// actually get dropped at runtime, not just asserted away by the type.
const stripHeavyCategoryContent = (
  category: ProductCategoryType
): ProductCategoryType => ({
  id: category.id,
  depth: category.depth,
  name: category.name,
  productsCount: category.productsCount,
  totalSlug: category.totalSlug,
  ageLimitMet: category.ageLimitMet,
  code: category.code,
  slug: category.slug,
  parentId: category.parentId,
  isVisible: category.isVisible
})

export const stripHeavyProductCategoryContent = <T extends AnyProductTypes>(
  product: T
): T => ({
  ...product,
  productCategories: product.productCategories?.map(stripHeavyCategoryContent),
  categoriesTree: product.categoriesTree?.map(stripHeavyCategoryContent),
  category: product.category
    ? stripHeavyCategoryContent(product.category)
    : undefined
})

export const stripHeavyProductCategoryContentFromList = <
  T extends AnyProductTypes
>(
  products: T[]
): T[] => products.map(stripHeavyProductCategoryContent)

export type CategoryAccSearchTypes = {
  categoryId: string
  categoryName: string
  items: ProductTypes[]
}

// "Promocje" always at the top, "Pozostałe" always at the bottom
export const compareCategoryGroups = (
  a: { categoryName: string },
  b: { categoryName: string }
) => {
  if (a.categoryName === b.categoryName) return 0

  if (a.categoryName === PROMOTIONS_CATEGORY_NAME) return -1
  if (b.categoryName === PROMOTIONS_CATEGORY_NAME) return 1
  if (a.categoryName === UNCATEGORIZED_CATEGORY_NAME) return 1
  if (b.categoryName === UNCATEGORIZED_CATEGORY_NAME) return -1

  return 0
}

export const getProductGroupCategory = (product?: {
  categoriesTree?: ProductCategoryType[]
}): { id: string; name: string } => {
  const categoriesTree = product?.categoriesTree || []

  return (
    categoriesTree[categoriesTree.length - 1] || {
      id: UNCATEGORIZED_CATEGORY_ID,
      name: UNCATEGORIZED_CATEGORY_NAME
    }
  )
}

export const sortProductsByCategory = (products: ProductTypes[]) => {
  if (!products) return []

  const result = products.reduce(
    (acc: CategoryAccSearchTypes[], product: ProductTypes) => {
      const category = getProductGroupCategory(product)

      const existingCategory = acc.find(
        (i: CategoryAccSearchTypes) => i.categoryName === category.name
      )

      if (existingCategory) {
        existingCategory.items.push(product)
      } else {
        const payload = {
          categoryId: category.id,
          categoryName: category.name,
          items: [product]
        }

        acc.push(payload)
      }

      return acc
    },
    []
  )

  return result.sort(compareCategoryGroups)
}

export const getUniqueCategories = (
  products?: ProductTypes[]
): CategoryTypeWithCounter<ProductCategoryType>[] => {
  if (!products) return []

  const categoriesMap = new Map<
    string,
    CategoryTypeWithCounter<ProductCategoryType>
  >()

  products.forEach(item => {
    const mainCategories = item.categoriesTree?.filter(
      category => category.depth === 1
    )

    mainCategories?.forEach(category => {
      if (categoriesMap.has(category.name)) {
        const existingCategory = categoriesMap.get(category.name)!

        existingCategory.count = (existingCategory.count || 0) + 1
      } else {
        const newCategory = {
          ...category,
          count: 1,
          productsCount: category.productsCount || 0
        }

        categoriesMap.set(category.name, newCategory)
      }
    })
  })

  return Array.from(categoriesMap.values())
}

export const mergeAndCustomizeSearchCategories = <
  T extends { name: string; productsCount?: number }
>(
  originalCategories: T[],
  filteredCategories: CategoryTypeWithCounter<{ name: string }>[] | null
): CategoryTypeWithCounter<T>[] =>
  filteredCategories
    ? originalCategories.reduce<CategoryTypeWithCounter<T>[]>(
        (acc, originalCategory) => {
          const matchingItem = filteredCategories.find(
            filteredCategory => filteredCategory.name === originalCategory.name
          )

          if (matchingItem) {
            const mergedItem = { ...originalCategory, ...matchingItem }

            acc.push(mergedItem)
          }

          return acc
        },
        []
      )
    : originalCategories

export const getProductsByCategories = (
  products: ProductTypes[],
  categoryCode: string
) => {
  return products.filter(product =>
    product?.categoriesTree?.some(cat => cat.code?.toString() === categoryCode)
  )
}
